import React, { useState } from "react";
import { motion } from "framer-motion";
import PageShell from "@/components/proof/PageShell";
import {
    BlinkingDot,
    MonoLabel,
    FieldButton,
} from "@/components/proof/primitives";
import { easing } from "@/lib/animations";

const RESOURCES = [
    {
        label: "Brand Lockup · SVG",
        size: "12 KB",
        format: "SVG",
    },
    {
        label: "Sygnet · Black & White",
        size: "8 KB",
        format: "SVG · PNG",
    },
    {
        label: "Field 001 Lookbook · Hi-res",
        size: "42 MB",
        format: "ZIP · 11 frames",
    },
    {
        label: "Editorial Imagery · Print",
        size: "68 MB",
        format: "ZIP · 24 frames",
    },
];

const PRESS_CHANNELS = [
    {
        title: "Press · Samples",
        body: "press@proofwear.store",
        sub: "For editorial features, sample loans and on-set requests.",
    },
    {
        title: "Stockists · Wholesale",
        body: "wholesale@proofwear.store",
        sub: "Line-sheet sent by request. Minimum order applies.",
    },
    {
        title: "Studio · Atelier",
        body: "studio@proofwear.store",
        sub: "Atelier visits in Warsaw by appointment only.",
    },
];

const COVERAGE = [
    { outlet: "Hypebeast", title: "Quiet Streetwear · The Polish School", date: "Jan 2026" },
    { outlet: "SSENSE Magazine", title: "On the Slow Atelier", date: "Dec 2025" },
    { outlet: "Highsnobiety", title: "Field 001 · A First Look", date: "Nov 2025" },
    { outlet: "032c", title: "Inside the Próżna Studio", date: "Oct 2025" },
];

export default function Press() {
    const [requested, setRequested] = useState(false);
    return (
        <PageShell
            sectionNumber="16"
            eyebrow="Editorial · Wholesale · Atelier"
            title="Press"
            crumb="Press"
            data-testid="page-press"
        >
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "clamp(48px, 6vw, 100px)",
                }}
            >
                {/* INTRO */}
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.9, ease: easing.primary }}
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 22,
                        maxWidth: "60ch",
                    }}
                    data-testid="press-intro"
                >
                    <p
                        style={{
                            margin: 0,
                            fontFamily: "var(--font-serif)",
                            fontStyle: "italic",
                            fontSize: "clamp(20px, 2.2vw, 32px)",
                            lineHeight: 1.4,
                            color: "var(--on-ink)",
                        }}
                    >
                        Proof is a quiet luxury streetwear studio based in
                        Warsaw. Every artifact is cut, sewn and finished in
                        our own atelier.
                    </p>
                    <p
                        style={{
                            margin: 0,
                            fontFamily: "var(--font-body)",
                            fontSize: "clamp(15px, 1.1vw, 17px)",
                            lineHeight: 1.65,
                            color: "var(--on-ink-soft)",
                        }}
                    >
                        We work with editors, stylists and stockists who share
                        the brand&rsquo;s commitment to slow manufacture and honest
                        construction. Sample loans, editorial features and
                        on-set requests are handled directly by the studio —
                        no agency in between.
                    </p>
                </motion.div>

                {/* CHANNELS */}
                <div
                    data-testid="press-channels"
                    style={{
                        display: "grid",
                        gridTemplateColumns:
                            "repeat(auto-fit, minmax(260px, 1fr))",
                        gap: "clamp(22px, 2.5vw, 40px)",
                        paddingTop: "clamp(24px, 3vw, 40px)",
                        borderTop: "1px solid var(--on-ink-line)",
                    }}
                >
                    {PRESS_CHANNELS.map((c, i) => (
                        <motion.div
                            key={c.title}
                            initial={{ opacity: 0, y: 18 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.4 }}
                            transition={{
                                duration: 0.75,
                                delay: 0.06 * i,
                                ease: easing.primary,
                            }}
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 12,
                                padding: "22px 22px",
                                border: "1px solid var(--on-ink-line)",
                                background:
                                    "var(--on-ink-surface, rgba(255,255,255,0.03))",
                            }}
                            data-testid={`press-channel-${i}`}
                        >
                            <MonoLabel tone="mute">{c.title}</MonoLabel>
                            <a
                                href={`mailto:${c.body}`}
                                style={{
                                    color: "var(--on-ink)",
                                    fontFamily: "var(--font-display)",
                                    fontSize: "clamp(18px, 1.6vw, 24px)",
                                    fontWeight: 600,
                                    letterSpacing: "-0.01em",
                                    textDecoration: "none",
                                }}
                            >
                                {c.body}
                            </a>
                            <p
                                style={{
                                    margin: 0,
                                    fontFamily: "var(--font-serif)",
                                    fontStyle: "italic",
                                    fontSize: 14,
                                    lineHeight: 1.55,
                                    color: "var(--on-ink-mute)",
                                }}
                            >
                                {c.sub}
                            </p>
                        </motion.div>
                    ))}
                </div>

                {/* DOWNLOAD KIT */}
                <section
                    data-testid="press-kit"
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 22,
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "baseline",
                            justifyContent: "space-between",
                            paddingBottom: 14,
                            borderBottom: "1px solid var(--on-ink-line)",
                            flexWrap: "wrap",
                            gap: 10,
                        }}
                    >
                        <h2
                            style={{
                                margin: 0,
                                fontFamily: "var(--font-display)",
                                fontWeight: 700,
                                fontSize: "clamp(26px, 3vw, 42px)",
                                letterSpacing: "-0.025em",
                                textTransform: "uppercase",
                                color: "var(--on-ink)",
                            }}
                        >
                            Press Kit
                        </h2>
                        <MonoLabel tone="mute">Updated Jan 2026</MonoLabel>
                    </div>
                    <ul
                        style={{
                            margin: 0,
                            padding: 0,
                            listStyle: "none",
                            display: "flex",
                            flexDirection: "column",
                            gap: 0,
                        }}
                    >
                        {RESOURCES.map((r, i) => (
                            <li
                                key={r.label}
                                style={{
                                    display: "grid",
                                    gridTemplateColumns:
                                        "minmax(0, 1.8fr) minmax(0, 1fr) auto",
                                    gap: 18,
                                    alignItems: "center",
                                    padding: "18px 0",
                                    borderBottom:
                                        "1px solid var(--on-ink-line)",
                                }}
                                data-testid={`press-kit-${i}`}
                            >
                                <span
                                    style={{
                                        fontFamily: "var(--font-display)",
                                        fontWeight: 500,
                                        fontSize: "clamp(15px, 1.2vw, 18px)",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.02em",
                                        color: "var(--on-ink)",
                                    }}
                                >
                                    {r.label}
                                </span>
                                <span
                                    style={{
                                        fontFamily: "var(--font-mono)",
                                        fontSize: 11,
                                        letterSpacing: "0.18em",
                                        textTransform: "uppercase",
                                        color: "var(--on-ink-mute)",
                                    }}
                                >
                                    {r.format} · {r.size}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => alert("Sample download — placeholder")}
                                    data-testid={`press-download-${i}`}
                                    style={{
                                        background: "transparent",
                                        color: "var(--on-ink)",
                                        border: "1px solid var(--on-ink-line-strong, rgba(255,255,255,0.35))",
                                        fontFamily: "var(--font-mono)",
                                        fontSize: 10,
                                        letterSpacing: "0.28em",
                                        textTransform: "uppercase",
                                        padding: "10px 16px",
                                        cursor: "pointer",
                                    }}
                                >
                                    Download ↓
                                </button>
                            </li>
                        ))}
                    </ul>
                </section>

                {/* COVERAGE */}
                <section
                    data-testid="press-coverage"
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 22,
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "baseline",
                            justifyContent: "space-between",
                            paddingBottom: 14,
                            borderBottom: "1px solid var(--on-ink-line)",
                        }}
                    >
                        <h2
                            style={{
                                margin: 0,
                                fontFamily: "var(--font-display)",
                                fontWeight: 700,
                                fontSize: "clamp(26px, 3vw, 42px)",
                                letterSpacing: "-0.025em",
                                textTransform: "uppercase",
                                color: "var(--on-ink)",
                            }}
                        >
                            Recent Coverage
                        </h2>
                        <MonoLabel tone="mute">Selected · 2025—2026</MonoLabel>
                    </div>
                    <ul
                        style={{
                            margin: 0,
                            padding: 0,
                            listStyle: "none",
                        }}
                    >
                        {COVERAGE.map((c, i) => (
                            <li
                                key={i}
                                style={{
                                    display: "grid",
                                    gridTemplateColumns:
                                        "minmax(120px, 200px) 1fr minmax(100px, auto)",
                                    gap: 18,
                                    padding: "20px 0",
                                    borderBottom:
                                        "1px solid var(--on-ink-line)",
                                    alignItems: "baseline",
                                }}
                                data-testid={`press-coverage-${i}`}
                            >
                                <span
                                    style={{
                                        fontFamily: "var(--font-mono)",
                                        fontSize: 11,
                                        letterSpacing: "0.24em",
                                        textTransform: "uppercase",
                                        color: "var(--on-ink-mute)",
                                    }}
                                >
                                    {c.outlet}
                                </span>
                                <span
                                    style={{
                                        fontFamily: "var(--font-display)",
                                        fontWeight: 500,
                                        fontSize: "clamp(16px, 1.3vw, 20px)",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.02em",
                                        color: "var(--on-ink)",
                                    }}
                                >
                                    {c.title}
                                </span>
                                <span
                                    style={{
                                        fontFamily: "var(--font-mono)",
                                        fontSize: 11,
                                        letterSpacing: "0.18em",
                                        textTransform: "uppercase",
                                        color: "var(--on-ink-mute)",
                                        textAlign: "right",
                                    }}
                                >
                                    {c.date}
                                </span>
                            </li>
                        ))}
                    </ul>
                </section>

                {/* SAMPLE REQUEST */}
                <section
                    data-testid="press-sample"
                    style={{
                        padding: "clamp(28px, 4vw, 48px) clamp(24px, 3vw, 40px)",
                        border: "1px solid var(--on-ink-line)",
                        background: "var(--on-ink-surface, rgba(255,255,255,0.03))",
                        display: "flex",
                        flexDirection: "column",
                        gap: 18,
                    }}
                >
                    <MonoLabel
                        tone="signal"
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 12,
                        }}
                    >
                        <BlinkingDot />
                        Sample loans · Field 002
                    </MonoLabel>
                    <h3
                        style={{
                            margin: 0,
                            fontFamily: "var(--font-display)",
                            fontWeight: 700,
                            fontSize: "clamp(22px, 2.4vw, 36px)",
                            letterSpacing: "-0.02em",
                            textTransform: "uppercase",
                            color: "var(--on-ink)",
                            maxWidth: "20ch",
                        }}
                    >
                        Field 002 samples ship to press from week 1.
                    </h3>
                    <p
                        style={{
                            margin: 0,
                            fontFamily: "var(--font-serif)",
                            fontStyle: "italic",
                            fontSize: 17,
                            lineHeight: 1.55,
                            color: "var(--on-ink-soft)",
                            maxWidth: "50ch",
                        }}
                    >
                        Editors and stylists can request samples from the
                        moment Field 002 closes its passcode gate. Write to
                        press@proofwear.store with your outlet, the date
                        window, and a sketch of the shoot.
                    </p>
                    {requested ? (
                        <MonoLabel tone="signal" data-testid="press-sample-ack">
                            Request noted — we will be in touch within two business days.
                        </MonoLabel>
                    ) : (
                        <FieldButton
                            href="mailto:press@proofwear.store?subject=Sample request · Field 002"
                            onClick={() => setRequested(true)}
                            data-testid="press-sample-cta"
                            style={{ alignSelf: "flex-start" }}
                        >
                            Request Sample Kit →
                        </FieldButton>
                    )}
                </section>
            </div>
        </PageShell>
    );
}
